import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type Pagination = {
  page: number;
  pageSize: number;
};

type PaginationHandlers = {
  handlePageChange: (newPage: number, newPageSize: number) => void;
  handlePageSizeChange: (_: number, size: number) => void;
};

/**
 * Similar to React setState, but specifically for pagination based components that uses the
 * URL search params as the source of truth
 * @returns An array containing the pagination, a function to set the new pagination parameters,
 * and handlers for Ant Design Pagination component
 */
export const usePagination = (): [
  Partial<Pagination>,
  (pagination: Pagination) => void,
  PaginationHandlers,
] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamPage = searchParams.get("page");
  const searchParamPageSize = searchParams.get("pageSize");
  const page =
    searchParamPage &&
    Number.isInteger(Number(searchParamPage)) &&
    Number(searchParamPage) > 0
      ? Number(searchParamPage)
      : undefined;
  const pageSize =
    searchParamPageSize &&
    Number.isInteger(Number(searchParamPageSize)) &&
    Number(searchParamPageSize) > 0
      ? Number(searchParamPageSize)
      : undefined;

  const pagination = useMemo(() => ({ page, pageSize }), [page, pageSize]);
  const setPagination = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", page.toString());
        next.set("pageSize", pageSize.toString());
        return next;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number, newPageSize: number) => {
      setPagination({ page: newPage, pageSize: newPageSize });
    },
    [setPagination]
  );

  const handlePageSizeChange = useCallback(
    (_: number, size: number) => {
      setPagination({ page: 1, pageSize: size });
    },
    [setPagination]
  );

  return [
    pagination,
    setPagination,
    { handlePageChange, handlePageSizeChange },
  ];
};
